import React, { useState, useEffect, useReducer, useRef, useContext } from "react";
import { isSameDay, parseISO, format, isToday, isYesterday, isValid } from "date-fns";
import { ptBR } from "date-fns/locale";
import openSocket from "../../services/socket-io";
import clsx from "clsx";

// Helper function to safely parse dates
const safeParse = (dateValue) => {
  if (!dateValue) return null;
  try {
    let date;
    if (dateValue instanceof Date) {
      date = dateValue;
    } else if (typeof dateValue === "string") {
      date = parseISO(dateValue);
    } else if (typeof dateValue === "number") {
      date = new Date(dateValue);
    } else {
      return null;
    }
    return isValid(date) ? date : null;
  } catch {
    return null;
  }
};

// Helper function to safely format dates
const safeFormat = (dateValue, formatStr, options = {}) => {
  const date = safeParse(dateValue);
  if (!date) return "";
  try {
    return format(date, formatStr, options);
  } catch {
    return "";
  }
};

// Helper function to force download a file
const downloadFile = async (url, fileName) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Error downloading file:", error);
    // Fallback to opening in new tab
    window.open(url, "_blank");
  }
};

import MarkdownWrapper from "../MarkdownWrapper";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import Audio from "../Audio";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getMediaUrl } from "../../config";

// Icons
const CheckIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const DoubleCheckIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M18 7l-1.41-1.41-6.34 6.34 1.41 1.41L18 7zm4.24-1.41L11.66 16.17 7.48 12l-1.41 1.41L11.66 19l12-12-1.42-1.41zM.41 13.41L6 19l1.41-1.41L1.83 12 .41 13.41z" />
  </svg>
);

const ClockIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const BlockIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
  </svg>
);

const DownloadIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const MoreVertIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
  </svg>
);

const FileIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else {
      state.push(newMessage);
    }

    return [...state];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate;
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const MessagesList = ({ ticketId, isGroup }) => {
  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastMessageRef = useRef();
  const { user } = useContext(AuthContext);

  const [selectedMessage, setSelectedMessage] = useState({});
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const currentTicketId = useRef(ticketId);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    currentTicketId.current = ticketId;
  }, [ticketId]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        try {
          const { data } = await api.get("/messages/" + ticketId, {
            params: { pageNumber },
          });

          if (currentTicketId.current === ticketId) {
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            setHasMore(data.hasMore);
            setLoading(false);
          }

          if (pageNumber === 1 && data.messages.length > 1) {
            scrollToBottom();
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchMessages();
    }, 500);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [pageNumber, ticketId]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("connect", () => socket.emit("joinChatBox", ticketId));

    socket.on("appMessage", (data) => {
      if (data.action === "create") {
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        scrollToBottom();
      }

      if (data.action === "update") {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [ticketId]);

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const scrollToBottom = () => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({});
    }
  };

  const handleScroll = (e) => {
    if (!hasMore) return;
    const { scrollTop } = e.currentTarget;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    e.preventDefault();
    setSelectedMessage(message);
    setMenuPosition({ x: e.clientX, y: e.clientY });
    setMenuOpen(true);
  };

  const handleCloseMessageOptionsMenu = () => {
    setMenuOpen(false);
  };

  const renderMessageAck = (message) => {
    if (message.ack === 0) {
      return <ClockIcon className="w-3.5 h-3.5 text-dark-400" />;
    }
    if (message.ack === 1) {
      return <CheckIcon className="w-3.5 h-3.5 text-dark-400" />;
    }
    if (message.ack === 2) {
      return <DoubleCheckIcon className="w-3.5 h-3.5 text-dark-400" />;
    }
    if (message.ack === 3 || message.ack === 4) {
      return <DoubleCheckIcon className="w-3.5 h-3.5 text-primary-500" />;
    }
  };

  const checkMessageMedia = (message) => {
    if (message.mediaType === "location" && message.body.split("|").length >= 2) {
      let locationParts = message.body.split("|");
      let imageLocation = locationParts[0];
      let linkLocation = locationParts[1];
      let descriptionLocation = null;

      if (locationParts.length > 2) descriptionLocation = message.body.split("|")[2];

      return (
        <LocationPreview
          image={imageLocation}
          link={linkLocation}
          description={descriptionLocation}
        />
      );
    } else if (message.mediaType === "vcard") {
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      return <VcardPreview contact={contact} numbers={obj[0]?.number} />;
    } else if (/^.*\.(jpe?g|png|gif)?$/i.exec(message.mediaUrl) && message.mediaType === "image") {
      return (
        <div className="rounded-lg overflow-hidden mb-1">
          <ModalImageCors imageUrl={getMediaUrl(message.mediaUrl)} />
        </div>
      );
    } else if (message.mediaType === "audio") {
      return (
        <div className="my-1">
          <Audio url={getMediaUrl(message.mediaUrl)} />
        </div>
      );
    } else if (message.mediaType === "video") {
      return (
        <video
          className="w-64 max-w-full rounded-lg mb-1"
          src={getMediaUrl(message.mediaUrl)}
          controls
        />
      );
    } else {
      // File attachment
      const fileName = message.mediaUrl?.split("/").pop() || "Arquivo";
      return (
        <div
          className={clsx(
            "flex items-center gap-3 p-3 rounded-lg mb-1 cursor-pointer",
            message.fromMe
              ? "bg-primary-600/20"
              : "bg-dark-100 dark:bg-dark-600"
          )}
          onClick={() => downloadFile(getMediaUrl(message.mediaUrl), fileName)}
        >
          <div className={clsx(
            "w-10 h-10 rounded-lg flex items-center justify-center",
            message.fromMe ? "bg-primary-500" : "bg-dark-300 dark:bg-dark-500"
          )}>
            <FileIcon className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className={clsx(
              "text-sm font-medium truncate",
              message.fromMe ? "text-white" : "text-dark-700 dark:text-dark-200"
            )}>
              {fileName.length > 20 ? fileName.substring(0, 20) + "..." : fileName}
            </p>
          </div>
          <DownloadIcon className={clsx(
            "w-5 h-5",
            message.fromMe ? "text-white/70" : "text-dark-400"
          )} />
        </div>
      );
    }
  };

  const formatDateLabel = (date) => {
    const parsedDate = safeParse(date);
    if (!parsedDate) return "";
    if (isToday(parsedDate)) {
      return `Hoje, ${format(parsedDate, "dd 'de' MMMM", { locale: ptBR })}`;
    }
    if (isYesterday(parsedDate)) {
      return `Ontem, ${format(parsedDate, "dd 'de' MMMM", { locale: ptBR })}`;
    }
    return format(parsedDate, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  };

  const renderDailyTimestamps = (message, index) => {
    if (index === 0) {
      return (
        <div key={`timestamp-${message.id}`} className="flex justify-center my-4">
          <span className="px-4 py-1.5 text-xs font-medium text-dark-600 dark:text-dark-300 bg-dark-200/80 dark:bg-dark-700 rounded-full">
            {formatDateLabel(messagesList[index].createdAt)}
          </span>
        </div>
      );
    }
    if (index < messagesList.length - 1) {
      let messageDay = safeParse(messagesList[index].createdAt);
      let previousMessageDay = safeParse(messagesList[index - 1].createdAt);

      if (messageDay && previousMessageDay && !isSameDay(messageDay, previousMessageDay)) {
        return (
          <div key={`timestamp-${message.id}`} className="flex justify-center my-4">
            <span className="px-4 py-1.5 text-xs font-medium text-dark-600 dark:text-dark-300 bg-dark-200/80 dark:bg-dark-700 rounded-full">
              {formatDateLabel(messagesList[index].createdAt)}
            </span>
          </div>
        );
      }
    }
    if (index === messagesList.length - 1) {
      return (
        <div
          key={`ref-${message.createdAt}`}
          ref={lastMessageRef}
          style={{ float: "left", clear: "both" }}
        />
      );
    }
  };

  const renderQuotedMessage = (message) => {
    return (
      <div
        className={clsx(
          "flex rounded-lg overflow-hidden mb-2",
          message.fromMe
            ? "bg-primary-600/30"
            : "bg-dark-100 dark:bg-dark-600"
        )}
      >
        <span
          className={clsx(
            "w-1 flex-none",
            message.quotedMsg?.fromMe ? "bg-primary-400" : "bg-dark-400"
          )}
        />
        <div className="p-2 text-xs max-w-[280px]">
          {!message.quotedMsg?.fromMe && (
            <span className={clsx(
              "font-medium block",
              message.fromMe ? "text-white/80" : "text-primary-500"
            )}>
              {message.quotedMsg?.contact?.name}
            </span>
          )}
          <span className={clsx(
            "line-clamp-2",
            message.fromMe ? "text-white/70" : "text-dark-500 dark:text-dark-400"
          )}>
            {message.quotedMsg?.body}
          </span>
        </div>
      </div>
    );
  };

  const renderMessages = () => {
    if (messagesList.length > 0) {
      const viewMessagesList = messagesList.map((message, index) => {
        const contactName = message.contact?.name || "Contato";
        const messageTime = safeFormat(message.createdAt, "HH:mm");

        // Received message (from contact)
        if (!message.fromMe) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}

              {/* Message Container */}
              <div className="flex items-end gap-2 mb-4 mr-auto max-w-[85%] md:max-w-[70%]">
                {/* Avatar */}
                {message.contact?.profilePicUrl ? (
                  <img
                    src={message.contact.profilePicUrl}
                    alt={contactName}
                    className="w-8 h-8 rounded-full object-cover flex-shrink-0 mb-1"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-semibold text-xs flex-shrink-0 mb-1">
                    {contactName.charAt(0).toUpperCase()}
                  </div>
                )}

                <div className="flex flex-col">
                  {/* Header: Name, Time, Check */}
                  <div className="flex items-center gap-2 mb-1 px-1">
                    <span className="text-sm font-semibold text-dark-800 dark:text-dark-200">
                      {contactName}
                    </span>
                    <span className="text-xs text-dark-400">•</span>
                    <span className="text-xs text-dark-400">{messageTime}</span>
                    {renderMessageAck(message)}
                  </div>

                  {/* Message Bubble */}
                  <div className="group relative">
                    <div className="bg-white dark:bg-dark-700 rounded-2xl rounded-bl-md px-4 py-2.5 shadow-sm border border-dark-100 dark:border-dark-600">
                      {(message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard") &&
                        checkMessageMedia(message)}
                      {message.quotedMsg && renderQuotedMessage(message)}
                      {message.body && (
                        <div className="text-sm text-dark-700 dark:text-dark-200 whitespace-pre-wrap break-words">
                          <MarkdownWrapper>{message.body}</MarkdownWrapper>
                        </div>
                      )}
                    </div>

                    {/* Options button */}
                    <button
                      className="absolute -right-6 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-dark-100 dark:hover:bg-dark-600 transition-opacity"
                      disabled={message.isDeleted}
                      onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                    >
                      <MoreVertIcon className="w-4 h-4 text-dark-400" />
                    </button>
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
        } else {
          // Sent message (from me)
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}

              {/* Message Container */}
              <div className="flex items-end gap-2 mb-4 ml-auto max-w-[85%] md:max-w-[70%]">
                <div className="flex flex-col items-end">
                  {/* Header: Check, Time, You */}
                  <div className="flex items-center gap-2 mb-1 px-1">
                    {renderMessageAck(message)}
                    <span className="text-xs text-dark-400">{messageTime}</span>
                    <span className="text-xs text-dark-400">•</span>
                    <span className="text-sm font-semibold text-dark-800 dark:text-dark-200">
                      Você
                    </span>
                  </div>

                  {/* Message Bubble */}
                  <div className="group relative">
                    {/* Options button */}
                    <button
                      className="absolute -left-6 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-dark-100 dark:hover:bg-dark-600 transition-opacity"
                      disabled={message.isDeleted}
                      onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                    >
                      <MoreVertIcon className="w-4 h-4 text-dark-400" />
                    </button>

                    <div className="bg-primary-500 rounded-2xl rounded-br-md px-4 py-2.5 shadow-sm">
                      {(message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard") &&
                        checkMessageMedia(message)}
                      {message.isDeleted && (
                        <div className="flex items-center gap-1 text-white/70 italic text-sm">
                          <BlockIcon className="w-4 h-4" />
                          <span>Mensagem apagada</span>
                        </div>
                      )}
                      {message.quotedMsg && renderQuotedMessage(message)}
                      {!message.isDeleted && message.body && (
                        <div className="text-sm text-white whitespace-pre-wrap break-words">
                          <MarkdownWrapper>{message.body}</MarkdownWrapper>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Avatar */}
                {user?.profilePicUrl ? (
                  <img
                    src={user.profilePicUrl}
                    alt="Você"
                    className="w-8 h-8 rounded-full object-cover flex-shrink-0 mb-1"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-semibold text-xs flex-shrink-0 mb-1">
                    {user?.name?.charAt(0)?.toUpperCase() || "U"}
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        }
      });
      return viewMessagesList;
    } else {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-dark-100 dark:bg-dark-700 flex items-center justify-center">
              <svg className="w-8 h-8 text-dark-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <p className="text-dark-500 dark:text-dark-400">Diga olá para seu novo contato!</p>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="relative flex flex-col flex-1 overflow-hidden bg-dark-50 dark:bg-dark-900">
      {/* Messages container */}
      <div
        id="messagesList"
        className="relative flex flex-col flex-1 p-4 md:p-6 overflow-y-auto"
        onScroll={handleScroll}
      >
        {messagesList.length > 0 ? renderMessages() : renderMessages()}
      </div>

      {/* Loading spinner */}
      {loading && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10">
          <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Message Options Menu */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-50"
          onClick={handleCloseMessageOptionsMenu}
        >
          <div
            className="absolute bg-white dark:bg-dark-800 rounded-lg shadow-lg border border-dark-200 dark:border-dark-700 py-1 min-w-[120px]"
            style={{
              top: menuPosition.y,
              left: menuPosition.x,
              transform: "translate(-50%, -100%)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <MessageOptionsMenu
              message={selectedMessage}
              menuOpen={menuOpen}
              handleClose={handleCloseMessageOptionsMenu}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesList;
